'use client'

import { RotateRight16, X16 } from '@vinted/monochrome-icons'
import { Button, Icon, Image, Loader } from '@vinted/web-ui'
import { isNumber } from 'lodash'
import { Component } from 'react'
import { injectIntl, WrappedComponentProps } from 'react-intl'

import { ItemThumbnailSize } from '@marketplace-web/shared/item-photo-data'
import {
  ImageOrientation,
  MediaSelectPhotoOrientation,
  MediaUploadPhotoModel,
} from '@marketplace-web/item-upload/media-upload-data'

import { getPhotoThumbnail } from '../../utils'
import Sortable from '../SortableList'

type OwnProps = {
  photo: MediaUploadPhotoModel
  onDelete: (id: number) => void
  index: number
  imageAlt: string
  isControlsHidden: boolean
  enableRotation: boolean
  setPhotoOrientation: (photoOrientation: MediaSelectPhotoOrientation) => void
}

type Props = OwnProps & WrappedComponentProps

type State = {
  rotation: number
  isLoading: boolean
}

class PhotoBox extends Component<Props, State> {
  state: Readonly<State> = {
    rotation: 0,
    isLoading: true,
  }

  translate = (id: string) =>
    this.props.intl.formatMessage({ id: `item_upload.media_select.${id}` })

  rotateCallback = (id: number, orientation: ImageOrientation) => () =>
    this.props.setPhotoOrientation({ id, orientation })

  rotateClockwise = (id: number) => () => {
    let rotation: number
    let { orientation } = this.props.photo

    if (!isNumber(orientation)) return

    if (this.state.rotation === 270) {
      rotation = 0
    } else {
      rotation = this.state.rotation + 90
    }

    if (orientation === ImageOrientation.Degree270) {
      orientation = ImageOrientation.Degree0
    } else {
      orientation += ImageOrientation.Degree90
    }

    this.setState({ rotation }, this.rotateCallback(id, orientation))
  }

  delete = (id: number) => () => {
    this.props.onDelete(id)
  }

  onImageLoad = () => {
    this.setState({ isLoading: false })
  }

  renderPhoto = (rotation: number, photoUrl: string) => {
    const { index, imageAlt } = this.props

    return (
      <div className="photo-box__image-container" role="button" tabIndex={0}>
        <div data-testid={`image-wrapper-${index}`} style={{ transform: `rotate(${rotation}deg)` }}>
          <Image
            src={photoUrl}
            scaling={Image.Scaling.Cover}
            ratio={Image.Ratio.Square}
            styling={Image.Styling.Rounded}
            onLoad={this.onImageLoad}
            alt={imageAlt}
          />
        </div>
      </div>
    )
  }

  renderControls = () => {
    const { photo, isControlsHidden, enableRotation, index } = this.props

    if (isControlsHidden) return null

    return (
      <>
        <div className="photo-box__delete-button">
          <Button
            testId={`media-select-grid-delete-button-${index}`}
            icon={<Icon name={X16} color={Icon.Color.GreyscaleLevel1} testId="x" />}
            size={Button.Size.Small}
            inverse
            styling={Button.Styling.Filled}
            inline
            onClick={this.delete(photo.id)}
            aria={{
              'aria-label': this.translate('a11y.delete_photo'),
            }}
          />
        </div>
        {enableRotation && (
          <div className="photo-box__action-button">
            <Button
              testId={`media-select-grid-rotate-button-${index}`}
              icon={
                <Icon
                  name={RotateRight16}
                  color={Icon.Color.GreyscaleLevel1}
                  testId="rotate-right"
                />
              }
              size={Button.Size.Small}
              inverse
              styling={Button.Styling.Filled}
              inline
              onClick={this.rotateClockwise(photo.id)}
              aria={{
                'aria-label': this.translate('a11y.rotate_photo'),
              }}
            />
          </div>
        )}
      </>
    )
  }

  render() {
    const { index, photo } = this.props

    return (
      <div className="photo-box">
        <Sortable.Item id={photo.id} index={index}>
          {this.renderPhoto(
            this.state.rotation,
            getPhotoThumbnail(photo, ItemThumbnailSize.XLarge) || photo.fullSizeUrl,
          )}
        </Sortable.Item>

        {!this.state.isLoading && this.renderControls()}

        {this.state.isLoading && (
          <div data-testid={`media-select-grid-item-${index}-loader`} className="photo-box__loader">
            <Loader size={Loader.Size.XLarge} />
          </div>
        )}
      </div>
    )
  }
}

export default injectIntl(PhotoBox)
